<template>
  <public-layout>

    <!-- ── Back nav ── -->
    <div class="sc-back-bar">
      <div class="sc-container">
        <a href="/listings" class="sc-back-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Listings
        </a>
      </div>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="sc-container sc-detail-loading">
      <div class="sc-detail-skeleton">
        <div class="skeleton" style="height:420px;border-radius:var(--radius-lg);margin-bottom:2rem;"></div>
        <div style="display:grid;grid-template-columns:1fr 340px;gap:2rem;">
          <div>
            <div class="skeleton" style="height:32px;width:70%;margin-bottom:1rem;border-radius:6px;"></div>
            <div class="skeleton" style="height:16px;width:90%;margin-bottom:.5rem;border-radius:4px;"></div>
            <div class="skeleton" style="height:16px;width:75%;margin-bottom:.5rem;border-radius:4px;"></div>
            <div class="skeleton" style="height:16px;width:80%;border-radius:4px;"></div>
          </div>
          <div>
            <div class="skeleton" style="height:240px;border-radius:var(--radius-lg);"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Error ── -->
    <div v-else-if="error" class="sc-container sc-error-state">
      <div class="sc-empty-state">
        <div class="sc-empty-state__icon">😕</div>
        <h3>Listing not found</h3>
        <p>This listing may have been removed or the link is invalid.</p>
        <a href="/listings" class="btn-primary">Browse Listings</a>
      </div>
    </div>

    <!-- ── Detail content ── -->
    <template v-else-if="item">

      <!-- Hero image -->
      <div class="sc-detail-hero">
        <img
          v-if="heroImage"
          :src="heroImage"
          :alt="itemTitle"
          class="sc-detail-hero__img sc-detail-hero__img--clickable"
          @error="onImgError"
          @click="openLightbox(galleryIdx)"
        />
        <button v-if="heroImage" class="sc-detail-hero__zoom-hint" @click="openLightbox(galleryIdx)" aria-label="View full image">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          Zoom
        </button>
        <div v-else class="sc-detail-hero__placeholder">
          <span>{{ formatType(type) }}</span>
        </div>
        <span class="card-badge sc-detail-hero__badge" :class="type">{{ formatType(type) }}</span>
        <span v-if="type === 'property' && item && item.is_sold" class="sc-detail-hero__sold">SOLD</span>
      </div>

      <!-- Main layout -->
      <div class="sc-container sc-detail-layout">

        <!-- ── LEFT: Main content ── -->
        <div class="sc-detail-main">

          <!-- Title & meta -->
          <h1 class="sc-detail-title">{{ itemTitle }}</h1>
          <div class="sc-detail-meta-row">
            <span class="sc-detail-meta-item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {{ itemLocation }}
            </span>
            <span class="sc-detail-meta-item" v-if="itemPrice">
              {{ itemPrice }}
            </span>
            <span class="sc-detail-meta-item">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {{ itemDate }}
            </span>
          </div>

          <!-- Image gallery (additional images) -->
          <div v-if="galleryImages.length > 1" class="sc-gallery">
            <div
              v-for="(img, idx) in galleryImages"
              :key="idx"
              class="sc-gallery__thumb"
              :class="{ active: galleryIdx === idx }"
              @click="galleryIdx = idx"
            >
              <img :src="getImageUrl(img)" :alt="itemTitle + ' image ' + (idx+1)" @error="onImgError" />
            </div>
          </div>

          <!-- Description -->
          <div class="sc-detail-section">
            <h2>About this listing</h2>
            <div class="sc-detail-description" v-html="itemDescription"></div>
          </div>

          <!-- Video embeds -->
          <div v-if="youtubeVideoId" class="sc-detail-section">
            <h2>Video</h2>
            <div class="sc-video-wrap">
              <iframe
                :src="'https://www.youtube.com/embed/' + youtubeVideoId"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                class="sc-video-frame"
              ></iframe>
            </div>
          </div>
          <div v-if="instagramShortcode" class="sc-detail-section">
            <h2>Instagram</h2>
            <div class="sc-video-wrap sc-video-wrap--instagram">
              <iframe
                :src="'https://www.instagram.com/p/' + instagramShortcode + '/embed'"
                frameborder="0"
                scrolling="no"
                allowtransparency="true"
                class="sc-video-frame sc-video-frame--instagram"
              ></iframe>
            </div>
          </div>

          <!-- Details table -->
          <div class="sc-detail-section" v-if="detailRows.length">
            <h2>Listing details</h2>
            <div class="sc-detail-table">
              <div v-for="row in detailRows" :key="row.label" class="sc-detail-row">
                <span class="sc-detail-row__label">{{ row.label }}</span>
                <span class="sc-detail-row__value">{{ row.value }}</span>
              </div>
            </div>
          </div>

          <!-- Project Team (guests can see — no login required) -->
          <div v-if="type === 'project' && projectTeam.length" class="sc-detail-section">
            <h2>Project Team</h2>
            <div class="sc-team-list">
              <div v-for="role in projectTeam" :key="role.label" class="sc-team-row">
                <div class="sc-team-role">{{ role.label }}</div>
                <div class="sc-team-members">
                  <a :href="getMemberUrl(role.lead)" class="sc-team-person">
                    <img
                      :src="getMemberAvatar(role.lead)"
                      :alt="role.lead.name"
                      class="sc-team-avatar"
                      @error="onAvatarError"
                    />
                    <div class="sc-team-person__info">
                      <span class="sc-team-person__name">{{ role.lead.name }}</span>
                      <span v-if="role.lead.profession" class="sc-team-person__sub">{{ role.lead.profession }}</span>
                    </div>
                  </a>
                  <a
                    v-for="member in role.teamMembers"
                    :key="member.id"
                    :href="getMemberUrl(member)"
                    class="sc-team-person sc-team-person--sm"
                  >
                    <img
                      :src="getMemberAvatar(member)"
                      :alt="member.name"
                      class="sc-team-avatar sc-team-avatar--sm"
                      @error="onAvatarError"
                    />
                    <span class="sc-team-person__name">{{ member.name }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Share section -->
          <div class="sc-detail-section">
            <h2>Share this listing</h2>
            <div class="sc-share-grid">
              <a :href="shareUrls.facebook" target="_blank" rel="noopener" class="share-btn facebook">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                Facebook
              </a>
              <a :href="shareUrls.twitter" target="_blank" rel="noopener" class="share-btn twitter">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                Twitter / X
              </a>
              <a :href="shareUrls.whatsapp" target="_blank" rel="noopener" class="share-btn whatsapp">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.83L0 24l6.348-1.5A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.807 9.807 0 0 1-4.996-1.366l-.358-.213-3.718.878.939-3.63-.234-.373A9.79 9.79 0 0 1 2.182 12c0-5.415 4.403-9.818 9.818-9.818 5.416 0 9.818 4.403 9.818 9.818 0 5.415-4.402 9.818-9.818 9.818z"/></svg>
                WhatsApp
              </a>
              <a :href="shareUrls.linkedin" target="_blank" rel="noopener" class="share-btn linkedin">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                LinkedIn
              </a>
              <button class="share-btn copy-link" @click="copyLink">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                {{ copied ? 'Copied!' : 'Copy link' }}
              </button>
            </div>
          </div>

        </div>

        <!-- ── RIGHT: Sidebar ── -->
        <aside class="sc-detail-sidebar">

          <!-- Owner actions -->
          <div v-if="isOwner && type === 'property'" class="sc-sidebar-card sc-owner-actions">
            <h4>Manage listing</h4>
            <a :href="'/post/' + item.slug" class="btn-outline sc-owner-btn">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit listing
            </a>
            <button
              class="sc-owner-btn"
              :class="item.is_sold ? 'btn-outline sc-owner-btn--unsold' : 'btn-outline sc-owner-btn--sold'"
              :disabled="soldLoading"
              @click="toggleSold"
            >
              <span v-if="soldLoading" class="sc-spinner"></span>
              <template v-else>
                <svg v-if="!item.is_sold" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {{ item.is_sold ? 'Mark as available' : 'Mark as sold' }}
              </template>
            </button>
          </div>

          <!-- User / Poster card -->
          <div class="sc-user-card" v-if="poster">
            <div class="sc-user-card__header">
              <img
                :src="getPosterAvatar()"
                :alt="poster.name"
                class="sc-user-card__avatar"
                @error="onImgError"
              />
              <div class="sc-user-card__info">
                <h3>{{ poster.name }}</h3>
                <p v-if="poster.profession">{{ poster.profession }}</p>
                <p v-else-if="poster.type === 'company' && poster.company">{{ poster.company.name }}</p>
              </div>
            </div>

            <div class="sc-user-card__details">
              <div v-if="posterLocation" class="sc-user-detail">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span>{{ posterLocation }}</span>
              </div>
              <div v-if="poster.email" class="sc-user-detail">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <span>{{ poster.email }}</span>
              </div>
              <div v-if="poster.created_at" class="sc-user-detail">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span>Member since {{ joinedDate }}</span>
              </div>
            </div>

            <div class="sc-user-card__actions">
              <a :href="'/sc/profile/' + poster.slug" class="btn-outline" style="flex:1;justify-content:center;">
                View Profile
              </a>
              <button class="btn-primary" style="flex:1;justify-content:center;" @click="contactPoster">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Message
              </button>
            </div>

            <div v-if="!isLoggedIn" class="sc-user-card__login-hint">
              <a href="#" @click.prevent="showLogin">Log in</a> to send a message
            </div>
          </div>

          <!-- Quick stats -->
          <div class="sc-sidebar-card" v-if="item">
            <h4>Listing stats</h4>
            <div class="sc-stat-row">
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                Likes
              </span>
              <button
                class="sc-like-btn"
                :class="{ 'sc-like-btn--liked': alreadyLiked }"
                :disabled="likeLoading || alreadyLiked || !isLoggedIn"
                @click="likeListing"
                :title="!isLoggedIn ? 'Sign in to like' : alreadyLiked ? 'You liked this' : 'Like this listing'"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" :fill="alreadyLiked ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {{ item.likes ? item.likes.length : 0 }}
              </button>
            </div>
            <div class="sc-stat-row">
              <span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                Comments
              </span>
              <strong>{{ item.comments ? item.comments.length : 0 }}</strong>
            </div>
          </div>

          <!-- Related listings link -->
          <div class="sc-sidebar-card">
            <h4>Browse more</h4>
            <div class="sc-related-links">
              <a :href="'/listings?type=' + type" class="sc-related-link">
                More {{ formatType(type) }}s
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </a>
              <a href="/listings" class="sc-related-link">
                All listings
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </a>
            </div>
          </div>

        </aside>
      </div>
    </template>

    <!-- Lightbox -->
    <transition name="lb-fade">
      <div
        v-if="lightboxOpen"
        class="lb-backdrop"
        @click.self="closeLightbox"
        @wheel.prevent="lbWheel"
        @mousemove="lbMousemove"
        @mouseup="lbMouseup"
        @mouseleave="lbMouseup"
        @touchstart.passive="lbTouchStart"
        @touchend.passive="lbTouchEnd"
      >
        <!-- Close -->
        <button class="lb-btn lb-close" @click="closeLightbox" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        <!-- Prev -->
        <button v-if="galleryImages.length > 1" class="lb-btn lb-nav lb-nav--prev" @click.stop="lbPrev" aria-label="Previous">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        <!-- Image -->
        <div
          class="lb-img-wrap"
          @mousedown="lbMousedown"
          @dblclick="lbDblclick"
        >
          <img
            :src="lbCurrentSrc"
            :style="lbImgStyle"
            class="lb-img"
            draggable="false"
            @error="onImgError"
          />
        </div>

        <!-- Next -->
        <button v-if="galleryImages.length > 1" class="lb-btn lb-nav lb-nav--next" @click.stop="lbNext" aria-label="Next">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>

        <!-- Zoom controls -->
        <div class="lb-zoom-bar" @click.stop>
          <button class="lb-zoom-btn" @click="lbZoomOut" :disabled="lbZoom <= 1" aria-label="Zoom out">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <span class="lb-zoom-level">{{ Math.round(lbZoom * 100) }}%</span>
          <button class="lb-zoom-btn" @click="lbZoomIn" :disabled="lbZoom >= 5" aria-label="Zoom in">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <button v-if="lbZoom > 1" class="lb-zoom-btn lb-reset-btn" @click="lbReset">Reset</button>
        </div>

        <!-- Counter -->
        <div v-if="galleryImages.length > 1" class="lb-counter">{{ lightboxIdx + 1 }} / {{ galleryImages.length }}</div>

        <!-- Thumbnails -->
        <div v-if="galleryImages.length > 1" class="lb-thumbs" @click.stop>
          <button
            v-for="(img, i) in galleryImages"
            :key="i"
            :class="['lb-thumb', i === lightboxIdx && 'lb-thumb--active']"
            @click="lbGoTo(i)"
          >
            <img :src="getImageUrl(img)" :alt="itemTitle + ' ' + (i + 1)" @error="onImgError" />
          </button>
        </div>

        <!-- Hint -->
        <div class="lb-hint">Scroll to zoom &bull; Drag to pan &bull; Double-click to fit</div>
      </div>
    </transition>

    <!-- Login modal (minimal, triggers redirect to messages) -->
    <div class="modal fade sc-modal" id="detail-login" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content sc-modal__content">
          <div class="sc-modal__header">
            <h3>Log in to message</h3>
            <button type="button" class="sc-modal__close" data-dismiss="modal">&times;</button>
          </div>
          <div class="sc-modal__body">
            <div v-if="loginError" class="sc-alert sc-alert--error">{{ loginError }}</div>
            <form id="detail-login-form" @submit.prevent="submitLogin">
              <div class="sc-field">
                <label>Email address</label>
                <input type="email" name="email" v-model="loginEmail" placeholder="you@example.com" required />
              </div>
              <div class="sc-field">
                <label>Password</label>
                <input type="password" name="password" v-model="loginPassword" placeholder="Your password" required />
              </div>
              <button type="submit" class="btn-primary sc-modal__submit" :disabled="loginLoading">
                <span v-if="loginLoading" class="sc-spinner"></span>
                <span v-else>Log in &amp; message</span>
              </button>
            </form>
            <div class="sc-modal__footer-links">
              <a href="/register">Create an account</a>
            </div>
          </div>
        </div>
      </div>
    </div>

  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, setJsonLd, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'ListingDetail',
  components: { PublicLayout },

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      apiUrl: process.env.VUE_APP_API_URL   || '/api/',
      type:   this.$route.params.type  || 'project',
      slug:   this.$route.params.slug  || '',
      item:   null,
      loading: true,
      error:   false,
      galleryIdx: 0,
      copied: false,

      loginEmail: '', loginPassword: '', loginLoading: false, loginError: '',
      soldLoading: false,
      likeLoading: false,
      currentUserId: localStorage.getItem('user'),

      lightboxOpen: false,
      lightboxIdx: 0,
      lbZoom: 1,
      lbPanX: 0,
      lbPanY: 0,
      lbDragging: false,
      lbDragStart: { x: 0, y: 0 },
      lbPanStart: { x: 0, y: 0 },
      lbTouchStartX: 0,
    };
  },

  computed: {
    isLoggedIn() { return !!this.currentUserId; },

    isOwner() {
      if (!this.isLoggedIn || !this.poster) return false;
      return this.poster.owner_id == this.currentUserId || this.poster.id == this.currentUserId;
    },

    alreadyLiked() {
      if (!this.currentUserId || !this.item || !this.item.likes) return false;
      return this.item.likes.some(l => String(l.user_id) === String(this.currentUserId));
    },

    poster() {
      if (!this.item) return null;
      return this.item.user || null;
    },

    posterLocation() {
      if (!this.poster) return '';
      const city    = this.poster.city    && this.poster.city.name;
      const state   = this.poster.state   && this.poster.state.name;
      const country = this.poster.country && this.poster.country.name;
      return [city || state, country].filter(Boolean).join(', ');
    },

    joinedDate() {
      if (!this.poster || !this.poster.created_at) return '';
      const d = new Date(this.poster.created_at);
      return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
    },

    itemTitle() {
      return (this.item && this.item.name) || 'Untitled';
    },

    itemDescription() {
      if (!this.item) return '';
      return this.item.description || this.item.about || this.item.summary || 'No description provided.';
    },

    itemLocation() {
      if (!this.item) return '';
      const city    = this.item.city    && this.item.city.name;
      const state   = this.item.state   && this.item.state.name;
      const country = this.item.country && this.item.country.name;
      return [city || state, country].filter(Boolean).join(', ') || 'Location N/A';
    },

    itemPrice() {
      if (!this.item) return '';
      const symbol = (this.item.currency && this.item.currency.symbol) || '';
      const fmt = v => symbol + Number(v).toLocaleString('en-US');
      if (this.item.prices && this.item.prices.length) {
        const p = this.item.prices[0];
        const val = p.price || p.amount;
        return val ? fmt(val) + (p.title ? ' — ' + p.title : '') : '';
      }
      if (this.item.price) return fmt(this.item.price);
      return '';
    },

    itemDate() {
      if (!this.item || !this.item.created_at) return '';
      return new Date(this.item.created_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });
    },

    heroImage() {
      if (!this.item) return '';
      const images = this.item.images || [];
      const idx = Math.min(this.galleryIdx, images.length - 1);
      const img = images[idx];
      if (!img) return '';
      return img.path || (img.image ? this.imgUrl + img.image : '');
    },

    galleryImages() {
      return (this.item && this.item.images) || [];
    },

    detailRows() {
      if (!this.item) return [];
      const rows = [];
      const add = (label, val) => { if (val) rows.push({ label, value: val }); };
      add('Type', this.formatType(this.type));
      add('Category', this.item.category && this.item.category.name);
      add('Status', this.item.status === 1 ? 'Active' : (this.item.status === 0 ? 'Pending' : ''));
      add('Location', this.itemLocation);
      add('Country', this.item.country && this.item.country.name);
      add('State', this.item.state && this.item.state.name);
      add('City', this.item.city && this.item.city.name);
      if (this.type === 'project') {
        add('Completion', this.item.completion_status);
        add('Value', this.item.project_value);
      }
      if (this.type === 'property') {
        add('Bedrooms',  this.item.bedroom);
        add('Bathrooms', this.item.bathroom);
        add('Size',      this.item.size);
        if (this.itemPrice) add('Price', this.itemPrice);
      }
      return rows;
    },

    youtubeVideoId() {
      const url = this.item && this.item.youtube_url;
      if (!url) return '';
      const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
      return m ? m[1] : '';
    },

    instagramShortcode() {
      const url = this.item && this.item.instagram_url;
      if (!url) return '';
      const m = url.match(/instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/);
      return m ? m[1] : '';
    },

    projectTeam() {
      if (!this.item || this.type !== 'project') return [];
      const roles = [
        { label: 'Client / Developer',    key: 'getclient',            teamKey: null },
        { label: 'Project Architect',     key: 'getarchitect',         teamKey: 'architectteam' },
        { label: 'Structural Engineer',   key: 'structuralengineer',   teamKey: 'structuralengineerteam' },
        { label: 'MEP Engineer',          key: 'mepengineer',          teamKey: 'mepengineerteam' },
        { label: 'Quantity Surveyor',     key: 'quantitysurveyor',     teamKey: 'quantitysurveyorteam' },
        { label: 'Main Contractor',       key: 'maincontractor',       teamKey: 'maincontractorteam' },
        { label: 'Mechanical Contractor', key: 'mechanicalcontractor', teamKey: 'mechanicalcontractorteam' },
        { label: 'Electrical Contractor', key: 'electricalcontractor', teamKey: 'electricalcontractorteam' },
        { label: 'Piling Contractor',     key: 'pilingcontractor',     teamKey: 'pilingcontractorteam' },
        { label: 'Lift Contractor',       key: 'liftcontractor',       teamKey: 'liftcontractorteam' },
        { label: 'Project Manager',       key: 'projectmanager',       teamKey: 'projectmanagerteam' },
      ];
      const result = [];
      for (const role of roles) {
        const lead = this.item[role.key];
        if (!lead) continue;
        const teamMembers = (role.teamKey && this.item[role.teamKey])
          ? this.item[role.teamKey].map(t => t.user).filter(Boolean)
          : [];
        result.push({ label: role.label, lead, teamMembers });
      }
      if (this.item.vendors && this.item.vendors.length) {
        const byCategory = {};
        for (const v of this.item.vendors) {
          if (!v.user) continue;
          const cat = (v.category && v.category.name) || 'Vendor';
          if (!byCategory[cat]) byCategory[cat] = [];
          byCategory[cat].push(v.user);
        }
        for (const [cat, users] of Object.entries(byCategory)) {
          result.push({ label: cat, lead: users[0], teamMembers: users.slice(1) });
        }
      }
      return result;
    },

    lbCurrentSrc() {
      const img = this.galleryImages[this.lightboxIdx];
      return img ? this.getImageUrl(img) : '';
    },

    lbImgStyle() {
      return {
        transform: `translate(${this.lbPanX}px, ${this.lbPanY}px) scale(${this.lbZoom})`,
        cursor: this.lbZoom > 1 ? (this.lbDragging ? 'grabbing' : 'grab') : 'zoom-in',
        transition: this.lbDragging ? 'none' : 'transform .2s ease',
      };
    },

    shareUrls() {
      const url  = encodeURIComponent(window.location.href);
      const text = encodeURIComponent('Check out this listing on Structurecity: ' + this.itemTitle);
      return {
        facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + url,
        twitter:  'https://twitter.com/intent/tweet?url=' + url + '&text=' + text,
        whatsapp: 'https://wa.me/?text=' + text + '%20' + url,
        linkedin: 'https://www.linkedin.com/sharing/share-offsite/?url=' + url,
      };
    },
  },

  mounted() {
    this.fetchDetail();
    this._lbKey = (e) => {
      if (!this.lightboxOpen) return;
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowRight') this.lbNext();
      if (e.key === 'ArrowLeft')  this.lbPrev();
    };
    document.addEventListener('keydown', this._lbKey);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this._lbKey);
  },

  methods: {
    fetchDetail() {
      this.loading = true;
      this.error   = false;
      let endpoint = '';
      const t = this.type;
      if (t === 'project')  endpoint = 'project/detail/'  + this.slug;
      else if (t === 'property') endpoint = 'property/details/' + this.slug;
      else if (t === 'plan')    endpoint = 'plan/details/'    + this.slug;
      else { this.error = true; this.loading = false; return; }

      this.axios.get(this.apiUrl + endpoint)
        .then(res => {
          if (res.status === 200 && res.data) {
            this.item = res.data;
            const img = this.item.images && this.item.images[0] ? this.item.images[0].path : null;
            const pageUrl = SITE_ORIGIN + '/listing/' + this.type + '/' + this.slug;
            useSEO({
              title: this.itemTitle + (this.itemLocation ? ' — ' + this.itemLocation : ''),
              description: this.itemDescription,
              image: img,
              url: pageUrl,
            });
            setJsonLd({
              '@context': 'https://schema.org',
              '@type': 'RealEstateListing',
              name: this.itemTitle,
              description: this.itemDescription.replace(/<[^>]*>/g, '').slice(0, 300),
              image: img || undefined,
              url: pageUrl,
              datePosted: this.item.created_at,
              ...(this.itemLocation ? { contentLocation: { '@type': 'Place', name: this.itemLocation } } : {}),
            });
          } else {
            this.error = true;
          }
        })
        .catch(() => { this.error = true; })
        .finally(() => { this.loading = false; });
    },

    getImageUrl(img) {
      if (!img) return '';
      return img.path || (img.image ? this.imgUrl + img.image : '');
    },

    getPosterAvatar() {
      const u = this.poster;
      if (!u) return 'https://placehold.co/80x80/e5e7eb/9ca3af?text=?';
      if (u.type === 'company' && u.company && u.company.logo) return this.imgUrl + 'users/' + u.company.logo;
      if (u.avatar) return this.$avatarSrc(u.avatar, 'users');
      return 'https://placehold.co/80x80/dbeafe/1d4ed8?text=' + encodeURIComponent((u.name || '?')[0].toUpperCase());
    },

    formatType(type) {
      return { project: 'Project', property: 'Property', post: 'Property', plan: 'Plan' }[type] || type;
    },

    onImgError(e) { e.target.src = 'https://placehold.co/800x500/e5e7eb/9ca3af?text=No+Image'; },
    onAvatarError(e) { e.target.src = '/assets/avatar.png'; },

    getMemberUrl(user) {
      if (!user || !user.slug) return '#';
      return user.type === 'company' ? '/company/' + user.slug : '/sc/profile/' + user.slug;
    },

    getMemberAvatar(user) {
      if (!user) return '/assets/avatar.png';
      if (user.avatar) return this.$avatarSrc(user.avatar, 'users');
      const initial = (user.name || '?')[0].toUpperCase();
      return 'https://placehold.co/48x48/dbeafe/1d4ed8?text=' + encodeURIComponent(initial);
    },

    copyLink() {
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2500);
      }).catch(() => {
        const el = document.createElement('textarea');
        el.value = window.location.href;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2500);
      });
    },

    openLightbox(idx) {
      this.lightboxIdx = idx !== undefined ? idx : 0;
      this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    },

    closeLightbox() {
      this.lightboxOpen = false;
      this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0;
      document.body.style.overflow = '';
    },

    lbPrev() {
      this.lightboxIdx = (this.lightboxIdx - 1 + this.galleryImages.length) % this.galleryImages.length;
      this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0;
    },

    lbNext() {
      this.lightboxIdx = (this.lightboxIdx + 1) % this.galleryImages.length;
      this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0;
    },

    lbGoTo(idx) {
      this.lightboxIdx = idx;
      this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0;
    },

    lbWheel(e) {
      const step = e.deltaY < 0 ? 0.25 : -0.25;
      const next = Math.min(5, Math.max(1, this.lbZoom + step));
      this.lbZoom = next;
      if (next === 1) { this.lbPanX = 0; this.lbPanY = 0; }
    },

    lbZoomIn()  { this.lbZoom = Math.min(5, +(this.lbZoom + 0.5).toFixed(2)); },
    lbZoomOut() {
      this.lbZoom = Math.max(1, +(this.lbZoom - 0.5).toFixed(2));
      if (this.lbZoom === 1) { this.lbPanX = 0; this.lbPanY = 0; }
    },
    lbReset()   { this.lbZoom = 1; this.lbPanX = 0; this.lbPanY = 0; },

    lbDblclick() {
      this.lbZoom > 1 ? this.lbReset() : (this.lbZoom = 2.5);
    },

    lbMousedown(e) {
      if (this.lbZoom <= 1) return;
      e.preventDefault();
      this.lbDragging = true;
      this.lbDragStart = { x: e.clientX, y: e.clientY };
      this.lbPanStart  = { x: this.lbPanX, y: this.lbPanY };
    },

    lbMousemove(e) {
      if (!this.lbDragging) return;
      this.lbPanX = this.lbPanStart.x + (e.clientX - this.lbDragStart.x);
      this.lbPanY = this.lbPanStart.y + (e.clientY - this.lbDragStart.y);
    },

    lbMouseup() { this.lbDragging = false; },

    lbTouchStart(e) { this.lbTouchStartX = e.touches[0].clientX; },
    lbTouchEnd(e) {
      const dx = e.changedTouches[0].clientX - this.lbTouchStartX;
      if (Math.abs(dx) > 50) { dx < 0 ? this.lbNext() : this.lbPrev(); }
    },

    likeListing() {
      if (this.alreadyLiked || this.likeLoading || !this.isLoggedIn) return;
      this.likeLoading = true;
      let endpoint = '';
      let payload = { user_id: this.currentUserId };
      if (this.type === 'project') {
        endpoint = 'project/like';
        payload.project_id = this.item.id;
      } else if (this.type === 'property') {
        endpoint = 'property/like';
        payload.post_id = this.item.id;
      } else if (this.type === 'plan') {
        endpoint = 'plan/like';
        payload.plan_id = this.item.id;
      }
      if (!endpoint) { this.likeLoading = false; return; }
      this.axios.post(this.apiUrl + endpoint, payload)
        .then(res => {
          if (res.status === 200) {
            if (!this.item.likes) this.item.likes = [];
            this.item.likes.push({ user_id: this.currentUserId });
            this.$toastr.s('Liked!');
          } else {
            this.$toastr.e(res.message || 'Could not like listing.');
          }
          this.likeLoading = false;
        })
        .catch(() => {
          this.$toastr.e('Could not like listing.');
          this.likeLoading = false;
        });
    },

    toggleSold() {
      this.soldLoading = true;
      this.axios.post(this.apiUrl + 'property/mark-sold', {
        id: this.item.id,
        is_sold: this.item.is_sold ? 0 : 1,
      })
        .then(response => {
          if (response.status === 200) {
            this.item.is_sold = !this.item.is_sold;
            this.$toastr.s(response.message);
          } else {
            this.$toastr.e(response.message);
          }
        })
        .catch(() => { this.$toastr.e('Could not update status'); })
        .finally(() => { this.soldLoading = false; });
    },

    contactPoster() {
      if (!this.isLoggedIn) {
        this.showLogin();
        return;
      }
      this.$router.push({ name: 'messages' });
    },

    showLogin() {
      if (window.$) {
        window.$('#detail-login').modal('show');
      } else {
        this.$router.push({ name: 'Login' });
      }
    },

    submitLogin() {
      this.loginError   = '';
      this.loginLoading = true;
      const data = new FormData(document.getElementById('detail-login-form'));
      this.axios.post(this.apiUrl + 'auth/login', data)
        .then(res => {
          if (res.status === 200) {
            localStorage.setItem('user', res.data.data.id);
            localStorage.setItem('slug', res.data.data.slug);
            localStorage.setItem('token', res.data.token);
            if (window.$) window.$('#detail-login').modal('hide');
            this.$router.push({ name: 'messages' });
          } else {
            this.loginError = res.message || 'Login failed.';
          }
        })
        .catch(() => { this.loginError = 'Login failed. Please try again.'; })
        .finally(() => { this.loginLoading = false; });
    },
  },
};
</script>

<style scoped>
/* ── Back bar ─────────────────────────────────────────── */
.sc-back-bar {
  background: var(--surface); border-bottom: 1px solid var(--border);
  padding: .75rem 0; margin-top: 68px;
}
.sc-back-link {
  display: inline-flex; align-items: center; gap: .375rem;
  color: var(--text-muted); text-decoration: none; font-size: .9rem; font-weight: 500;
  transition: color .15s;
}
.sc-back-link:hover { color: var(--primary); text-decoration: none; }

/* ── Hero image ───────────────────────────────────────── */
.sc-detail-hero {
  position: relative; background: var(--surface-3);
  max-height: 520px; overflow: hidden;
}
.sc-detail-hero__img {
  width: 100%; max-height: 520px; object-fit: cover; display: block;
}
.sc-detail-hero__placeholder {
  height: 320px; display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase;
  letter-spacing: .08em;
}
.sc-detail-hero__badge {
  position: absolute; top: 1.25rem; left: 1.25rem; font-size: .8125rem;
}
.sc-detail-hero__sold {
  position: absolute; top: 1.25rem; right: 1.25rem;
  background: #dc2626; color: #fff;
  font-size: .8125rem; font-weight: 800; letter-spacing: .1em;
  padding: 5px 14px; border-radius: 999px;
}

/* ── Loading / error ──────────────────────────────────── */
.sc-detail-loading  { padding: 2rem 1.5rem 4rem; }
.sc-detail-skeleton { max-width: 1100px; }
.sc-error-state { padding: 4rem 1.5rem; }
.sc-empty-state { text-align: center; padding: 3rem 1rem; }
.sc-empty-state__icon { font-size: 3rem; margin-bottom: 1rem; }
.sc-empty-state h3 { font-size: 1.25rem; font-weight: 700; color: var(--text); margin: 0 0 .5rem; }
.sc-empty-state p  { color: var(--text-muted); margin: 0 0 1.5rem; }

/* ── Main layout ──────────────────────────────────────── */
.sc-detail-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 2.5rem;
  padding: 2.5rem 1.5rem 4rem;
  align-items: start;
}

/* ── Main content ─────────────────────────────────────── */
.sc-detail-title {
  font-size: clamp(1.5rem, 3vw, 2.125rem); font-weight: 800;
  color: var(--text); margin: 0 0 1rem; letter-spacing: -.025em;
}
.sc-detail-meta-row {
  display: flex; align-items: center; flex-wrap: wrap; gap: 1rem;
  margin-bottom: 1.5rem;
}
.sc-detail-meta-item {
  display: flex; align-items: center; gap: .375rem;
  font-size: .9rem; color: var(--text-muted); font-weight: 500;
}

/* ── Gallery ──────────────────────────────────────────── */
.sc-gallery {
  display: flex; gap: .625rem; flex-wrap: wrap; margin-bottom: 2rem;
}
.sc-gallery__thumb {
  width: 80px; height: 60px; border-radius: var(--radius-sm);
  overflow: hidden; cursor: pointer; border: 2px solid transparent;
  transition: border-color .15s, transform .15s;
}
.sc-gallery__thumb.active { border-color: var(--primary); }
.sc-gallery__thumb:hover  { transform: translateY(-1px); }
.sc-gallery__thumb img { width: 100%; height: 100%; object-fit: cover; }

/* ── Detail sections ──────────────────────────────────── */
.sc-detail-section { margin-bottom: 2.5rem; }
.sc-detail-section h2 {
  font-size: 1.125rem; font-weight: 700; color: var(--text);
  margin: 0 0 1rem; padding-bottom: .625rem; border-bottom: 1px solid var(--border-light);
}
.sc-detail-description {
  color: var(--text-muted); font-size: .9375rem; line-height: 1.75; margin: 0;
}
.sc-detail-description :deep(p)  { margin: 0 0 .75rem; }
.sc-detail-description :deep(ul),
.sc-detail-description :deep(ol) { padding-left: 1.5rem; margin: 0 0 .75rem; }
.sc-detail-description :deep(h1),
.sc-detail-description :deep(h2),
.sc-detail-description :deep(h3) { color: var(--text); font-weight: 700; margin: 1rem 0 .5rem; }
.sc-detail-description :deep(strong) { color: var(--text); font-weight: 600; }
.sc-detail-description :deep(a) { color: var(--primary); }

/* ── Detail table ─────────────────────────────────────── */
.sc-detail-table {
  border: 1px solid var(--border); border-radius: var(--radius);
  overflow: hidden;
}
.sc-detail-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: .75rem 1rem; font-size: .9rem;
}
.sc-detail-row:not(:last-child) { border-bottom: 1px solid var(--border-light); }
.sc-detail-row:nth-child(even)  { background: var(--surface-2); }
.sc-detail-row__label { color: var(--text-muted); font-weight: 500; }
.sc-detail-row__value { color: var(--text); font-weight: 600; text-align: right; }

/* ── Share grid ───────────────────────────────────────── */
.sc-share-grid {
  display: flex; flex-wrap: wrap; gap: .625rem;
}

/* ── Sidebar ──────────────────────────────────────────── */
.sc-detail-sidebar { display: flex; flex-direction: column; gap: 1.25rem; position: sticky; top: 88px; }

/* ── User card ────────────────────────────────────────── */
.sc-user-card {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-lg); padding: 1.5rem; box-shadow: var(--shadow-sm);
}
.sc-user-card__header {
  display: flex; align-items: flex-start; gap: 1rem; margin-bottom: 1.25rem;
}
.sc-user-card__avatar {
  width: 64px; height: 64px; border-radius: 50%; object-fit: cover;
  background: var(--surface-3); flex-shrink: 0;
}
.sc-user-card__info h3 { font-size: 1.0625rem; font-weight: 700; margin: 0 0 .25rem; color: var(--text); }
.sc-user-card__info p  { font-size: .875rem; color: var(--text-muted); margin: 0; }
.sc-user-card__details { margin-bottom: 1.25rem; display: flex; flex-direction: column; gap: .625rem; }
.sc-user-detail {
  display: flex; align-items: flex-start; gap: .5rem;
  font-size: .875rem; color: var(--text-muted); line-height: 1.4;
}
.sc-user-detail svg { flex-shrink: 0; margin-top: 1px; }
.sc-user-card__actions { display: flex; gap: .625rem; }
.sc-user-card__login-hint {
  text-align: center; margin-top: .875rem;
  font-size: .8125rem; color: var(--text-muted);
}
.sc-user-card__login-hint a { color: var(--primary); font-weight: 600; text-decoration: none; }
.sc-user-card__login-hint a:hover { text-decoration: underline; }

/* ── Sidebar cards ────────────────────────────────────── */
.sc-sidebar-card {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius-lg); padding: 1.25rem; box-shadow: var(--shadow-sm);
}
.sc-sidebar-card h4 {
  font-size: .875rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: var(--text-muted); margin: 0 0 1rem;
}
.sc-stat-row {
  display: flex; align-items: center; justify-content: space-between;
  font-size: .9rem; color: var(--text-muted); padding: .5rem 0;
}
.sc-stat-row:not(:last-child) { border-bottom: 1px solid var(--border-light); }
.sc-stat-row span { display: flex; align-items: center; gap: .375rem; }
.sc-stat-row strong { color: var(--text); font-weight: 700; }
.sc-related-links { display: flex; flex-direction: column; gap: .25rem; }
.sc-related-link {
  display: flex; align-items: center; justify-content: space-between;
  padding: .625rem .75rem; border-radius: var(--radius-sm);
  text-decoration: none; color: var(--text); font-size: .9rem; font-weight: 500;
  transition: background .15s, color .15s;
}
.sc-related-link:hover { background: var(--surface-3); color: var(--primary); text-decoration: none; }

/* ── Owner actions ────────────────────────────────────── */
.sc-owner-actions { display: flex; flex-direction: column; gap: .625rem; }
.sc-owner-btn {
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  width: 100%; padding: .625rem 1rem; font-size: .9rem; border-radius: var(--radius-sm);
}
.sc-owner-btn--sold { border-color: #dc2626; color: #dc2626; }
.sc-owner-btn--sold:hover { background: #fee2e2; border-color: #dc2626; color: #dc2626; }
.sc-owner-btn--unsold { border-color: var(--success, #059669); color: var(--success, #059669); }
.sc-owner-btn--unsold:hover { background: #d1fae5; }

/* ── Modals ─────────────────────────────────────────────*/
.sc-modal .modal-dialog { max-width: 420px; }
.sc-modal__content {
  border: none !important; border-radius: var(--radius-lg) !important;
  box-shadow: var(--shadow-xl); overflow: hidden;
}
.sc-modal__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.75rem 1.75rem 0;
}
.sc-modal__header h3 { font-size: 1.25rem; font-weight: 700; color: var(--text); margin: 0; }
.sc-modal__close {
  background: none; border: none; font-size: 1.5rem;
  color: var(--text-muted); cursor: pointer; line-height: 1; padding: .25rem;
}
.sc-modal__body { padding: 1.5rem 1.75rem 1.75rem; }
.sc-modal__submit {
  width: 100%; justify-content: center; padding: .75rem;
  font-size: 1rem; border-radius: var(--radius-sm); margin-top: .25rem;
}
.sc-modal__footer-links {
  text-align: center; margin-top: 1.25rem; font-size: .875rem; color: var(--text-muted);
}
.sc-modal__footer-links a { color: var(--primary); font-weight: 600; text-decoration: none; }
.sc-field { margin-bottom: 1rem; }
.sc-field label { display: block; font-size: .875rem; font-weight: 500; margin-bottom: .375rem; }
.sc-field input {
  width: 100%; padding: .625rem .875rem; border: 1.5px solid var(--border);
  border-radius: var(--radius-sm); font-family: var(--font); font-size: .9375rem;
  color: var(--text); background: var(--surface); outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.sc-field input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1); }
.sc-alert { padding: .75rem 1rem; border-radius: var(--radius-sm); font-size: .875rem; margin-bottom: 1rem; }
.sc-alert--error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.sc-spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid rgba(0,0,0,.1); border-top-color: currentColor;
  border-radius: 50%; animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Project Team ────────────────────────────────────── */
.sc-team-list { display: flex; flex-direction: column; gap: 0; }
.sc-team-row {
  display: flex; align-items: flex-start; gap: 1.25rem;
  padding: .875rem 0; border-bottom: 1px solid var(--border-light);
}
.sc-team-row:last-child { border-bottom: none; }
.sc-team-role {
  flex: 0 0 180px; font-size: .8125rem; font-weight: 600; text-transform: uppercase;
  letter-spacing: .05em; color: var(--text-muted); padding-top: .5rem;
}
.sc-team-members { display: flex; flex-wrap: wrap; gap: .625rem; flex: 1; }
.sc-team-person {
  display: flex; align-items: center; gap: .625rem;
  padding: .375rem .75rem .375rem .375rem;
  background: var(--surface-2); border: 1px solid var(--border-light);
  border-radius: var(--radius); text-decoration: none; color: var(--text);
  font-size: .875rem; font-weight: 500; transition: background .15s, border-color .15s;
}
.sc-team-person:hover { background: var(--surface-3); border-color: var(--primary); text-decoration: none; color: var(--primary); }
.sc-team-person--sm { padding: .25rem .625rem .25rem .25rem; }
.sc-team-avatar {
  width: 40px; height: 40px; border-radius: 50%; object-fit: cover;
  background: var(--surface-3); flex-shrink: 0;
}
.sc-team-avatar--sm { width: 28px; height: 28px; }
.sc-team-person__info { display: flex; flex-direction: column; gap: .1rem; }
.sc-team-person__name { line-height: 1.3; }
.sc-team-person__sub { font-size: .75rem; color: var(--text-muted); font-weight: 400; }

/* ── Responsive ─────────────────────────────────────────*/
@media (max-width: 900px) {
  .sc-detail-layout { grid-template-columns: 1fr; }
  .sc-detail-sidebar { position: static; }
}
@media (max-width: 600px) {
  .sc-share-grid { flex-direction: column; }
  .sc-user-card__actions { flex-direction: column; }
  .sc-team-row { flex-direction: column; gap: .625rem; }
  .sc-team-role { flex: none; padding-top: 0; }
}

/* ── Hero click hint ─────────────────────────────────── */
.sc-detail-hero__img--clickable { cursor: zoom-in; }
.sc-detail-hero__zoom-hint {
  position: absolute; bottom: 1rem; right: 1rem;
  display: flex; align-items: center; gap: .375rem;
  background: rgba(0,0,0,.55); color: #fff;
  border: none; border-radius: 999px;
  font-size: .8rem; font-weight: 600; padding: .35rem .75rem;
  cursor: pointer; backdrop-filter: blur(4px);
  transition: background .15s;
}
.sc-detail-hero__zoom-hint:hover { background: rgba(0,0,0,.75); }

/* ── Lightbox ────────────────────────────────────────── */
.lb-backdrop {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,.93);
  display: flex; align-items: center; justify-content: center;
  user-select: none;
}
.lb-img-wrap {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.lb-img {
  max-width: 90vw; max-height: 80vh;
  object-fit: contain; display: block;
  transform-origin: center center;
  pointer-events: none;
}
.lb-btn {
  position: absolute; z-index: 10;
  background: rgba(255,255,255,.12); border: none; color: #fff;
  border-radius: 50%; width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; backdrop-filter: blur(4px);
  transition: background .15s, transform .15s;
}
.lb-btn:hover { background: rgba(255,255,255,.25); transform: scale(1.08); }
.lb-btn:disabled { opacity: .35; pointer-events: none; }
.lb-close { top: 1rem; right: 1rem; }
.lb-nav { top: 50%; transform: translateY(-50%); }
.lb-nav:hover { transform: translateY(-50%) scale(1.08); }
.lb-nav--prev { left: 1rem; }
.lb-nav--next { right: 1rem; }

/* Zoom bar */
.lb-zoom-bar {
  position: absolute; bottom: 72px; left: 50%; transform: translateX(-50%);
  display: flex; align-items: center; gap: .5rem;
  background: rgba(0,0,0,.6); border-radius: 999px;
  padding: .35rem .75rem; backdrop-filter: blur(6px);
}
.lb-zoom-btn {
  background: none; border: none; color: #fff;
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .15s;
}
.lb-zoom-btn:hover:not(:disabled) { background: rgba(255,255,255,.15); }
.lb-zoom-btn:disabled { opacity: .35; pointer-events: none; }
.lb-zoom-level {
  font-size: .8rem; font-weight: 700; color: #fff;
  min-width: 40px; text-align: center;
}
.lb-reset-btn {
  width: auto; padding: 0 .625rem;
  font-size: .75rem; font-weight: 600; border-radius: 999px;
  background: rgba(255,255,255,.15); color: #fff;
}

/* Counter */
.lb-counter {
  position: absolute; top: 1rem; left: 50%; transform: translateX(-50%);
  font-size: .8125rem; font-weight: 600; color: rgba(255,255,255,.8);
  background: rgba(0,0,0,.45); border-radius: 999px;
  padding: .25rem .75rem; pointer-events: none;
}

/* Thumbnail strip */
.lb-thumbs {
  position: absolute; bottom: 0; left: 0; right: 0;
  display: flex; gap: .5rem; justify-content: center; align-items: center;
  padding: .625rem 1rem; overflow-x: auto;
  background: linear-gradient(transparent, rgba(0,0,0,.7));
}
.lb-thumb {
  flex-shrink: 0; width: 56px; height: 42px;
  border: 2px solid transparent; border-radius: 4px;
  overflow: hidden; cursor: pointer; padding: 0;
  background: none; opacity: .6;
  transition: opacity .15s, border-color .15s;
}
.lb-thumb:hover  { opacity: .9; }
.lb-thumb--active { border-color: #fff; opacity: 1; }
.lb-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Hint */
.lb-hint {
  position: absolute; top: 1rem; left: 1rem;
  font-size: .75rem; color: rgba(255,255,255,.45);
  pointer-events: none;
}

/* Transition */
.lb-fade-enter-active, .lb-fade-leave-active { transition: opacity .2s; }
.lb-fade-enter-from, .lb-fade-leave-to { opacity: 0; }

@media (max-width: 600px) {
  .lb-nav--prev { left: .375rem; }
  .lb-nav--next { right: .375rem; }
  .lb-hint { display: none; }
}

.sc-video-wrap {
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: #000;
}
.sc-video-wrap--instagram {
  padding-top: 130%;
  max-width: 540px;
  background: var(--surface-2);
}
.sc-video-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.sc-like-btn {
  display: inline-flex; align-items: center; gap: .35rem;
  padding: .25rem .65rem; border-radius: var(--radius-full, 999px);
  border: 1.5px solid var(--border-light, #e5e7eb);
  background: none; cursor: pointer; font-size: .8125rem; font-weight: 600;
  color: var(--text-muted, #6b7280);
  transition: background .15s, color .15s, border-color .15s;
}
.sc-like-btn:hover:not(:disabled) { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
.sc-like-btn--liked { background: #fee2e2; color: #dc2626; border-color: #fca5a5; cursor: default; }
.sc-like-btn:disabled:not(.sc-like-btn--liked) { opacity: .55; cursor: not-allowed; }
</style>
